// /services/turfai/workflowResolver.ts
import { WORKFLOW_REGISTRY, WorkflowDefinition } from './workflowRegistry';
import { dmsActivityService, Activity } from './dmsActivityService';

export interface ResolvedWorkflow {
  id: string;
  definition: WorkflowDefinition;
  environment: string;
  activity?: Activity;
}

export interface WorkflowSearchParams {
  name: string;
  version?: string;
  environment?: string;
  status?: string;
}

export class WorkflowResolver {
  private cache: Map<string, ResolvedWorkflow> = new Map();
  private cacheTimeout = 5 * 60 * 1000; // 5 minutes
  private cacheTimestamps: Map<string, number> = new Map();
  
  constructor(private environment: string = 'development') {}
  
  async resolveWorkflow(logicalName: string): Promise<ResolvedWorkflow> {
    const cacheKey = `${this.environment}:${logicalName}`;
    
    // 1. Check cache with timeout
    if (this.isCacheValid(cacheKey)) {
      return this.cache.get(cacheKey)!;
    }
    
    // 2. Get registry definition
    const definition = WORKFLOW_REGISTRY[logicalName];
    if (!definition) {
      throw new Error(`Unknown workflow: ${logicalName}. Check WORKFLOW_REGISTRY.`);
    }
    
    // 3. Find workflow by exact name matching
    console.log(`🔍 Resolving workflow '${logicalName}' → looking for name: '${definition.name}'`);
    const workflow = await dmsActivityService.findWorkflowByName(definition.name);
    
    if (!workflow) {
      throw new Error(`Workflow not found: '${definition.name}' for logical name '${logicalName}'. Ensure the workflow exists in TurfAI platform.`);
    }
    
    // 4. Create resolved workflow
    const resolved: ResolvedWorkflow = {
      id: workflow.id,
      definition,
      environment: this.environment,
      activity: workflow
    };
    
    console.log(`✅ Resolved workflow '${logicalName}' → ID: ${workflow.id}`);
    this.setCacheEntry(cacheKey, resolved);
    return resolved;
  }
  
  async resolveMultiStageWorkflow(logicalName: string): Promise<ResolvedWorkflow[]> {
    const primaryWorkflow = await this.resolveWorkflow(logicalName);
    
    if (primaryWorkflow.definition.type === 'single-stage') {
      return [primaryWorkflow];
    }
    
    // For multi-stage workflows, resolve each stage
    const stages: ResolvedWorkflow[] = [];
    for (const stageName of primaryWorkflow.definition.stages || []) {
      try {
        const stageWorkflow = await this.resolveWorkflow(stageName);
        stages.push(stageWorkflow);
      } catch (error) {
        console.warn(`Could not resolve stage '${stageName}' for workflow '${logicalName}':`, error);
        // Continue with available stages rather than failing completely
      }
    }
    
    return stages.length > 0 ? stages : [primaryWorkflow];
  }
  
  // findWorkflowByDefinition removed - using direct name matching now
  
  // getEnvironmentOverride removed - using direct workflow mapping only
  
  private isCacheValid(cacheKey: string): boolean {
    if (!this.cache.has(cacheKey)) return false;
    
    const timestamp = this.cacheTimestamps.get(cacheKey);
    if (!timestamp) return false;
    
    return Date.now() - timestamp < this.cacheTimeout;
  }
  
  private setCacheEntry(cacheKey: string, resolved: ResolvedWorkflow): void {
    this.cache.set(cacheKey, resolved);
    this.cacheTimestamps.set(cacheKey, Date.now());
  }
  
  // Health check method
  async validateWorkflowRegistry(): Promise<{ valid: boolean; errors: string[] }> {
    const errors: string[] = [];
    
    for (const [logicalName] of Object.entries(WORKFLOW_REGISTRY)) {
      try {
        await this.resolveWorkflow(logicalName);
      } catch (error) {
        errors.push(`Failed to resolve '${logicalName}': ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }
    
    return {
      valid: errors.length === 0,
      errors
    };
  }
  
  // Get all available workflows
  getAvailableWorkflows(): { logicalName: string; definition: WorkflowDefinition }[] {
    return Object.entries(WORKFLOW_REGISTRY).map(([logicalName, definition]) => ({
      logicalName,
      definition
    }));
  }
  
  // Get workflows by type
  getWorkflowsByType(type: 'single-stage' | 'multi-stage'): { logicalName: string; definition: WorkflowDefinition }[] {
    return this.getAvailableWorkflows().filter(({ definition }) => definition.type === type);
  }
  
  // Clear cache (useful for testing)
  clearCache(): void {
    this.cache.clear();
    this.cacheTimestamps.clear();
  }
}

// Singleton instance
export const workflowResolver = new WorkflowResolver(
  import.meta.env.VITE_ENVIRONMENT || 'development'
);